/**
 * The scenes a technician can put up while they talk.
 *
 * A conversation is prose, and prose is the one thing this project does not
 * let a model write about physics. TALK.md's compromise has been that a
 * mechanism question gets an honest "I'd have to run that" and a pointer at
 * the tool's panel. This is the other half of that answer: the technician
 * cannot state what would happen, but they CAN reach over and start a
 * simulation the student watches for themselves.
 *
 * THE RULE IS UNCHANGED, and this is why. The model does not write the scene
 * and does not parameterise it - it NAMES one, from a list of ids the brief
 * handed it, and the client resolves that name against the same list before
 * anything mounts. An id that was not offered is not a scene; it is stripped
 * out of the sentence and forgotten. That is the same boundary the assistant's
 * router works behind, where a routing "names one of the REGISTERED intents
 * and carries only params those intents declared" - enforced at the edge
 * rather than trusted in the middle.
 *
 * WHAT A SCENE IS AND IS NOT. Every one of these runs a general case at
 * nominal settings: a sheath over a wafer edge, a crystal at 1200 K, air down
 * a bay. None of them is wired to the tool the technician is standing at, and
 * none carries a reading off its screen. So a scene DEMONSTRATES and never
 * reports, the card says so in as many words, and the prompt forbids the
 * technician from quoting a number out of one. A student who could not tell
 * the difference would be worse off than with no scene at all.
 *
 * METADATA ONLY, deliberately. The components live in
 * `app/talk/demoViews.tsx`, which nothing outside the panel imports: the
 * brief, the wire and the check harness all need the ids and none of them
 * should drag three.js in to get them. `npm run talk` asserts the two files
 * still agree.
 */

export interface DemoDef {
  /** Stable handle. Also the i18n key under `talk.demo.<id>`. */
  id: string
  /**
   * Who may show it.
   *
   * A tool's own scene belongs to the technician standing at that tool, for
   * the reason the directory is positions-only: what a machine is doing is
   * its operator's to say. A PRINCIPLE belongs to nobody - silicon is silicon
   * at every bay - so those are `'all'`, and it is how Jake and Emily have
   * anything to show at all, their modules having no field scene yet.
   */
  who: 'all' | readonly string[]
  /**
   * Words that pick this scene when the hosted model is unreachable.
   *
   * The offline technician is keyword-matched from the catalogue (see
   * `localReply`), and a scene is the one thing the template path can offer
   * that is not a template: every solver here is local, so a classroom with
   * no network still gets the simulation, just introduced by a duller
   * sentence. Multilingual for the same reason `localReply`'s own keywords
   * are - offline there is no model to understand a paraphrase.
   */
  match: readonly RegExp[]
}

/**
 * Seven principles anyone can show, then the scenes that belong to a tool,
 * grouped by the person standing at it.
 *
 * Ordered general-first, which is also the order they go into the brief: a
 * model scanning the list meets the portable ones before the local ones.
 * Within one person's scenes the order is also the offline match order, so
 * the narrower scene goes first where two could claim a question.
 */
export const DEMOS: readonly DemoDef[] = [
  /*
   * HOW THESE PATTERNS ARE WRITTEN, after a review found half of them firing
   * on the wrong questions (2026-09-17).
   *
   *  - ONE PATTERN PER LANGUAGE, and a word that is only a word in one
   *    language lives only in that one. The crystal scene carried Spanish
   *    "red" (lattice) in a pattern that also ran against English, so
   *    "stored", "infrared" and "glow red" all put up a crystal.
   *  - SHORT TOKENS GET WORD BOUNDARIES. `OES` under the i flag matched
   *    "does" and "goes"; `sem` matched "semiconductor"; `hop` "shop";
   *    `lag` "flag"; `iones` "emisiones". CJK has no word boundaries, so a
   *    CJK token is a compound (两个字 or more), never a lone character.
   *  - A PERSON'S OWN SCENES ARE TRIED BEFORE THE PRINCIPLES (matchDemo,
   *    2026-09-18), each group in registry order. So a tool's pattern must
   *    not claim a principle's bare word - Tom's drive-in declines a
   *    question that says "diffuse", Laura's absorption one that says
   *    "high-k" - and between two scenes of one person the narrower goes
   *    first in the registry (photon counting before the resist, the dopant
   *    atom before the doped lattice, emission before the sheath).
   *  - A PRINCIPLE STILL STEPS ASIDE FOR A TOOL'S OWN WORDS, for everyone who
   *    does NOT own that tool's scene: "quantum well" asked of Sarah should
   *    put up nothing rather than a tunnelling packet. Those exclusions are
   *    a leading negative lookahead, `^(?!.*cue)`.
   *  - PROCESS VOCABULARY IS NOT PHYSICS. A bare "barrier" is a barrier
   *    layer under copper or a gowning-room barrier; a bare "surface" is
   *    where the etch rate is low. Tunnelling wants "potential barrier" or an
   *    electron beside it; the surface scene wants reconstruction, dangling
   *    bonds or "silicon surface".
   *
   * `npm run talk` holds every scene to THREE tables through the offline
   * technician's own path - the original, a reviewer's written without
   * sight of these patterns, and one of non-"why" question shapes - plus a
   * list of questions that must reach nothing.
   *
   */
  {
    id: 'crystal-lattice',
    who: 'all',
    match: [
      /^(?!.*\b(lattice (vibration|wave|mode)s?|phonons?|raman|dopants?|doped|boron|phosphorus|arsenic|lattice sites?|strained|surfaces?|dimers?|hop\w*|diffus\w*)\b).*\b(crystal(line)?|lattice|diamond structure|unit cell)\b/i,
      /^(?!.*(vibraci[oó]n de (la )?red|fon[oó]n|raman|dopad|dopante|\bboro\b|f[oó]sforo|ars[eé]nico|tensad|superficie|difus|difund|\bsalt)).*\b(cristal(es|ina|ino)?|red cristalina|celda unidad|celda unitaria)\b/i,
      /^(?!.*(晶格振动|晶格振動|声子|聲子|拉曼|掺杂|摻雜|应变|應變|表面|跳跃|跳躍|扩散|擴散)).*(晶体|晶體|晶格|原子排列)/,
    ],
  },
  {
    id: 'bands',
    who: 'all',
    match: [
      /\b(band ?gap|band structure|conduction band|valence band|semiconduct\w*)\b/i,
      /\b(banda prohibida|estructura de bandas|banda de conducci[oó]n|banda de valencia|semiconduct\w*)\b/i,
      /(?<!子)能带|(?<!子)能帶|能隙|禁带|禁帶|半导体|半導體/,
    ],
  },
  {
    id: 'carriers',
    who: 'all',
    /*
     * Not the bare word "dopant". A dopant is equally the subject of the
     * hop scene below - "how does a dopant diffuse" is a question about
     * diffusion - and matching it here took every such question, because
     * first match wins in registry order. What belongs to this scene is
     * how MANY carriers a doping level gives, not what a dopant does.
     */
    match: [
      /\b(carriers?|freeze-?out|intrinsic|doping (level|concentration)|resistivity)\b/i,
      /\b(portador(es)?|resistividad|nivel de dopado|concentraci[oó]n de dopado|congelaci[oó]n de portadores)\b/i,
      /载流子|載流子|掺杂浓度|摻雜濃度|电阻率|電阻率/,
    ],
  },
  {
    id: 'si-surface',
    who: 'all',
    // Ryan's reaction scene owns the chemistry that happens AT a surface.
    match: [
      /^(?!.*\b(silane|chemisorb\w*|ligands?|passivat\w*|react\w*|etch\w*|rate|polish\w*|clean\w*)\b).*(\b(dangling bonds?|reconstruct\w*|adsorb\w*|dimers?)\b|\bsurface (atoms?|structure|states?|energy|reconstruction)\b|\b(silicon|crystal) surfaces?\b|surface of (the )?(silicon|crystal))/i,
      /^(?!.*(silano|quimisorci|ligando|pasivad|reacci|reacciona|grabado|pulid)).*(\b(enlaces? colgantes?|reconstru\w*|adsorci[oó]n|adsorbe\w*)\b|superficie (del|de) (silicio|cristal))/i,
      /^(?!.*(硅烷|矽烷|化学吸附|化學吸附|钝化|鈍化|反应|反應|刻蚀|蝕刻)).*(悬挂键|懸掛鍵|重构|重構|吸附|硅表面|矽表面|晶体表面|晶體表面|表面原子|表面结构|表面結構|表面.{0,4}(活泼|活潑|活性))/,
    ],
  },
  {
    id: 'diffusion-hop',
    who: 'all',
    match: [
      /^(?!.*\b(diffusion barriers?|barrier layers?|diffuser)\b).*\b(diffus\w*|hop(s|ping)?|arrhenius|activation energy)\b/i,
      /^(?!.*(barrera de difusi[oó]n|capa barrera)).*\b(difusi[oó]n|difunde\w*|saltos?|arrhenius|energ[ií]a de activaci[oó]n)\b/i,
      /^(?!.*(扩散阻挡|擴散阻擋|阻挡层|阻擋層)).*(扩散(?!开)|擴散(?!開)|跳跃|跳躍|激活能|活化能)/,
    ],
  },
  {
    id: 'film-growth',
    who: 'all',
    // Ryan's reaction scene owns how a molecule sticks by making a bond.
    match: [
      /^(?!.*\b(silane|chemisorb\w*|ligands?|bonds?|react\w*)\b).*\b(film grow\w*|deposit\w*|nucleat\w*|stick(s|ing)?)\b/i,
      /^(?!.*(silano|quimisorci|ligando|enlace|reacci)).*\b(crece|crecimiento de (la )?pel[ií]cula|deposici[oó]n|deposita|nucleaci[oó]n)\b/i,
      /^(?!.*(硅烷|矽烷|化学吸附|化學吸附|成键|成鍵)).*(成膜|沉积|沉積|薄膜生长|薄膜生長)/,
    ],
  },
  {
    id: 'tunnelling',
    who: 'all',
    /*
     * A wave packet smashing into a wall in a box: the Quantum Modeler's 2D
     * box (tools/TunnelBoxView), with the fraction that gets through charted
     * beside it. A principle, not a tool's - the oxide is grown at THRM-04,
     * but the wave that reaches through it is the same at every bay - and
     * the ONE tunnelling scene in the chat: the 1D instrument with the
     * thickness experiment stays in the furnace panel, because an eighth
     * principle would push the longest scene list past its share of the
     * brief (npm run talk guards that). "Leak" on its own is deliberately
     * not here: a chamber leak is a vacuum question.
     *
     * "Quantum" steps aside for the tool scenes that are also quantum - a
     * well, a channel, light counted in quanta.
     */
    match: [
      /^(?!.*\b(quantum (wells?|dots?|confinement|efficiency|yield|of light)|inversion|subbands?|photons?|shot noise)\b).*(\btunnel\w*|\bquantum\b|wave ?packet|hit(s|ting)? (a|the) wall|through (a|the) wall|\b(potential|energy|oxide|tunnel\w*) barrier\b|\bbarrier (height|width|thickness)\b|gate (oxide )?leak|oxide leak|leakage current|leaky (gate|oxide)|\bhigh-?k\b|\bhafni\w*|thin(ner)? (gate )?oxide|oxide\b.*\b(thinner|too thin)|through (the|an?) (oxide|insulator|barrier)|\belectrons?\b.*\bbarrier\b|\bbarrier\b.*\belectrons?\b|(oxide|insulator)\b.*\bleak)/i,
      /^(?!.*(pozos? cu[aá]ntic|confinamiento|inversi[oó]n|subbanda|fot[oó]n)).*(\bt[uú]nel\b|\bcu[aá]ntic\w*|paquete de ondas|atraviesa\w* (la|una) pared|\bbarrera (de potencial|de energ[ií]a|del? [oó]xido)\b|fuga de (puerta|[oó]xido|compuerta)|corriente de fuga|fuga\w*\b.*[oó]xido|[oó]xido\b.*(fuga|delgado|fino)|[oó]xido (m[aá]s )?delgado|\bhafnio\b|alta[ -]k\b|atravesar (un|el|la) (aislante|[oó]xido|barrera)|atravies\w* (la|una) barrera|electr[oó]n\w*\b.*\bbarrera)/i,
      /^(?!.*(量子阱|量子井|量子化|反型层|反轉層|子能带|子能帶|光子)).*(隧穿|穿隧|隧道效应|隧道效應|量子|波包|势垒|位障|穿墙|穿牆|栅漏|閘漏|栅氧|閘氧|漏电|漏電|高k|高介电|高介電|铪|鉿|穿过(绝缘|氧化)|穿過(絕緣|氧化))/,
    ],
  },
  // EPI-20's own. Owen's, because the box in it is a thickness he grows:
  // the one place on the floor where a recipe time decides an energy.
  {
    id: 'quantum-well',
    who: ['owen'],
    match: [
      /\b(quantum wells?|confine\w*|bound states?|energy levels?|particle in a box|sige|silicon.?germanium|heterojunction|strained)\b|why .*(thin|thickness) matter|thickness\b.*\bmatter|nanomet(re|er)s? thick|\bthin layers?\b|trap\w* (an? |the )?(electron|hole)s?|(electron|hole)s? (get |are )?trapped/i,
      /\b(pozos? cu[aá]ntic\w*|confina\w*|estados? ligados?|niveles? de energ[ií]a|sige|silicio.?germanio|heterouni[oó]n|tensad[oa]s?|part[ií]cula en una caja|capas? delgadas?|atrapa\w*)\b|importa el espesor|espesor\b.*\bimporta/i,
      /量子阱|量子井|束缚|束縛|能级|能階|硅锗|矽鍺|异质结|異質接面|应变层|應變層|盒中粒子|厚度为什么|厚度為什麼|为什么.*厚度|為什麼.*厚度|厚度.*重要|薄层|薄層|困住/,
    ],
  },
  {
    id: 'photon-counting',
    who: ['jake'],
    match: [
      /^(?!.*\babsorb).*(\b(photons?|shot noise|quanta|quantum of light|stochastic\w*|line.?edge roughness|rough\w*|LER|LWR|grainy|nois(y|ier|e)|poisson)\b|count(ing)? photons)/i,
      /^(?!.*\babsor[bc]).*\b(fot[oó]n(es)?|ruido de disparo|cuantos de luz|estoc[aá]stic\w*|rugos\w*|granulad\w*|ruidos[oa]s?|contar fotones|poisson)\b/i,
      /^(?!.*吸收).*(光子|散粒噪声|散粒雜訊|量子化|随机缺陷|隨機缺陷|粗糙|颗粒感|顆粒感|泊松|卜瓦松|噪声|雜訊)/,
    ],
  },
  // LITH-03's own, and the first thing this tool has had of its own to
  // show. Jake's: the resist answering a wavelength is what his exposure
  // is FOR, and the reason the fab's history runs down the spectrum.
  // Photon COUNTING - roughness, shot noise - is his other scene, and this
  // one steps aside for it.
  {
    id: 'resist-absorption',
    who: ['jake'],
    match: [
      /\bresists?\b|\bphotoresists?\b|\bphotoacid\b|\bsensiti[sz]\w*|\babsorb\w*|why .*\b(193|248|365|436)\b|\b(193|248|365|436)\b.*\b(193|248|365|436)\b|shorter wavelengths?|wavelengths?\b.*\b(shorter|smaller|down)|one wavelength|\bexcimer\b|\bi-?line\b|\bg-?line\b|\bkrf\b|\barf\b|\bchromophores?\b|\bconjugat\w*/i,
      /\bresinas?\b|fotorresin|fotorresist|fotoácido|fotoacido|sensibiliz|\babsorb\w*|absorci[oó]n|longitud(es)? de onda m[aá]s cortas?|\b(193|248|365|436)\b|exc[ií]mero|conjugaci/i,
      /光刻胶|光阻|感光|光酸|吸收|波长更短|波長更短|更短的波长|更短的波長|准分子|準分子|共轭|共軛|发色团|發色團|(193|248|365).*(193|248|365)/,
    ],
  },
  // DEP-02's own chemistry, and the one scene on the floor where a bond
  // actually breaks. Ryan's, because a reaction is the process talking and
  // the process is his.
  {
    id: 'chemisorption',
    who: ['ryan'],
    match: [
      /\bchemisorb\w*|react\w* (with|on|at) the (wafer|surface)|\breacting\b|bonds? (form|break)|breaks? a bond|\bbond(s|ing)? (to|with) (the )?(silicon|surface|wafer)|\bligands?\b|how does .*\b(stick|attach)|dangling bonds?|\bsilane\b|\bpassivat\w*|\bprecursors?\b.*\b(react|stick|bond|attach)/i,
      /quimisorci|reacciona\w* con (la oblea|la superficie)|reacci[oó]n en la superficie|enlace (se forma|se rompe)|se forma un enlace|forma\w* (un )?enlace|enlace con el silicio|romper un enlace|romp\w* (un|el) enlace|se rompe (un|el) enlace|\bligandos?\b|\bsilano\b|pasivad|se pega/i,
      /化学吸附|化學吸附|与晶圆反应|與晶圓反應|表面反应|表面反應|和表面反应|和表面反應|成键|成鍵|断键|斷鍵|配体|配體|硅烷|矽烷|钝化|鈍化|悬挂键|懸掛鍵|粘到|黏到|键.{0,4}断|鍵.{0,4}斷|断开|斷開/,
    ],
  },
  // MET-05's. Laura's rather than everybody's: they are principles, but
  // they are the principles her instruments are BUILT on - what a wave can
  // resolve, what the index she fits is made of, what a Raman line
  // measures - and a scene list is a menu, not a library.
  {
    id: 'electron-diffraction',
    who: ['laura'],
    match: [
      /\b(diffract\w*|interfer\w*|two slits|double slit|wavelength limit|resolve|resolution|SEM|electron (wave|beam|microscop\w*)|wave.?particle|optical microscopes?)\b|why can'?t .*\b(see|image)\b|electrons?\b.*\b(waves?|instead of light)\b|\bwaves?\b.*\belectrons?\b/i,
      /\b(difracci[oó]n|interf[ei]\w*|dos rendijas|doble rendija|resoluci[oó]n|onda de electrones|haz de electrones|microscopio [oó]ptico)\b|electr[oó]n\w*\b.*\b(ondas?|en vez de luz|en lugar de luz)|no puede ver/i,
      /衍射|繞射|干涉|双缝|雙縫|波长极限|波長極限|分辨率|分辨|解析度|电子波|電子波|电子束|電子束|扫描电镜|掃描電鏡|显微镜|顯微鏡|电子.*是波|電子.*是波|电子.*波吗|電子.*波嗎/,
    ],
  },
  {
    id: 'light-absorption',
    who: ['laura'],
    match: [
      /^(?!.*\b(high-?k|leak\w*|tunnel\w*)\b).*(\b(absorb\w*|refractive index|index of refraction|transparent|opaque|optical constants?|dielectric (function|constant)|ellipsomet\w*|n and k)\b|why.*\b(red|blue|green|purple|colou?r)\b|looks? (red|blue|green|purple)|band ?gap.*\blight\b|\blight\b.*band ?gap)/i,
      /^(?!.*(alta[ -]k|fuga|t[uú]nel)).*(\b(absorb\w*|transparente|opac[oa]|constantes? [oó]pticas?|funci[oó]n diel[eé]ctrica|elips[oó]metr\w*)\b|absorci[oó]n|[ií]ndice de refracci[oó]n|se ve (azul|verde|roj[oa]|morad[oa])|de qu[eé] color)/i,
      /^(?!.*(高k|漏电|漏電|隧穿|穿隧)).*(吸收|折射率|透明|不透明|光学常数|光學常數|介电函数|介電函數|椭偏|橢偏|为什么是红|為什麼是紅|颜色|顏色|蓝色|藍色|绿色|綠色)/,
    ],
  },
  {
    id: 'raman-strain',
    who: ['laura'],
    match: [
      /\b(raman|phonons?|lattice vibrations?|strain (gauge|measure\w*|metrology)|measure\w* (the )?strain|stress (in|of) (the )?(channel|film|liner)|520|wavenumbers?|dispersion)\b|\b(atoms?|crystal|lattice)\b.*\bvibrat\w*|\bvibrat\w*\b.*\b(atoms?|crystal|lattice)\b/i,
      /\b(raman|fon[oó]n(es)?|vibraci[oó]n(es)? de (la )?red|medir (la )?(deformaci[oó]n|tensi[oó]n)|se mide la (deformaci[oó]n|tensi[oó]n)|deformaci[oó]n|tensi[oó]n (en|del) (el )?(canal|capa|silicio)|n[uú]mero de onda|dispersi[oó]n)\b|vibra\w*\b.*\b([aá]tomos|red|cristal)|([aá]tomos|red|cristal)\b.*\bvibra/i,
      /拉曼|声子|聲子|晶格.{0,6}振动|晶格.{0,6}振動|原子.{0,6}振动|原子.{0,6}振動|应变|應變|测应力|測應力|应力|應力|波数|波數|色散/,
    ],
  },
  // IMP-10's three. The dopant ATOM - one electron held to one donor, and
  // what happens when there are too many of them - is tried before the
  // doped lattice, which owns where a dopant SITS: "how big is a dopant
  // atom" is this one, "where does the dopant atom go" is that one.
  {
    id: 'dopant-atom',
    who: ['nicole'],
    match: [
      /\b(hydrogen(ic)?( atom)?|bohr radius|ioni[sz](e|ation) energy|mott|impurity band|metal-insulator|degenerate(ly)? dop\w*|too much doping|dop(e|ed|ing) too (heavily|much)|heavily doped|central cell|donor electrons?|electron orbits?|orbit around)\b|why .*\b(dopants?|donors?|acceptors?)\b.*\b(bound|ioni[sz]\w*)\b|\b(donors?|acceptors?)\b.*\bioni[sz]\w*|how (big|large) is a (dopant|donor)|size of a (dopant|donor)|like a metal/i,
      /[aá]tomo de hidr[oó]geno|[oó]rbita|\b(radio de bohr|energ[ií]a de ionizaci[oó]n|mott|banda de impurezas|metal-aislante|dopado degenerado|demasiado dopado|dopa\w* demasiado|muy dopado|como un metal|electr[oó]n del donador|tama[nñ]o de un dopante)\b|donador(es)?\b.*\bioniza/i,
      /氢原子|氫原子|像氢|像氫|玻尔半径|波耳半徑|电离能|游離能|电离|游離|莫特|杂质能带|雜質能帶|金属绝缘体|金屬絕緣體|简并掺杂|簡併摻雜|重掺杂|重摻雜|掺杂太多|摻雜太多|像金属|像金屬|施主电子|施主電子|电子轨道|電子軌道|施主.*轨道|施主.*軌道|掺杂原子有多大|摻雜原子有多大/,
    ],
  },
  {
    id: 'doped-lattice',
    who: ['nicole'],
    /*
     * Not "crystal" or "lattice" on their own: crystal-lattice is a
     * principle everyone can show, it is tried first, and a student asking
     * what a crystal IS should get the plain one. What belongs here is the
     * DOPANT in it - the atom her tool put there, and where it sits.
     */
    match: [
      /\b(dopant atoms?|doped (crystal|lattice|silicon)|substitution\w*|molecular dynamics|lattice sites?)\b|\b(boron|dopants?|phosphorus|arsenic)\b.*\b(sits?|goes|go|ends? up|lattice|site)\b/i,
      /[aá]tomos? dopantes?|\b(dopad[oa] (cristal|red|silicio)|sustituci[oó]n|sustitucional|din[aá]mica molecular|sitios? de (la )?red)\b|\b(boro|dopante|f[oó]sforo|ars[eé]nico)\b.*\b(se coloca|va|queda|sitio|red)\b/i,
      /掺杂原子|摻雜原子|替位|置换位|置換位|掺杂后的晶体|摻雜後的晶體|分子动力学|分子動力學|(硼|磷|砷).*(晶格|位置|格点|格點)/,
    ],
  },
  {
    id: 'channelling',
    who: ['nicole'],
    /*
     * The vocabulary IMP-10's own router matches (assistant/tools/implant.ts,
     * 'imp-channelling'), so the question that runs the experiment at the
     * panel is the question that puts up the scene in the aisle.
     *
     * Not the bare word "crystal", which belongs to crystal-lattice and is
     * tried first: someone asking what a crystal IS should get the crystal,
     * and someone asking why their ions went down one should say channel,
     * tilt, or deeper.
     */
    match: [
      /\b(channel\w*|tilt\w*|twist\w*|deeper than|critical angle|lindhard|seven degrees|7 degrees)\b/i,
      /\b(canaliza\w*|inclinaci[oó]n|m[aá]s profundo)\b|[aá]ngulo cr[ií]tico/i,
      /沟道|溝道|通道效应|通道效應|倾斜|傾斜|临界角|臨界角|更深|拖尾/,
    ],
  },
  {
    id: 'plasma-emission',
    who: ['sarah'],
    match: [
      /\b(endpoint|emission|emits?|spectromet\w*|spectrum|glow\w*|excit(ed|ation|es)|electron temperature|spectral lines?|OES|purple|violet|pink)\b|what colou?r|colou?r of the plasma|what line|\bwavelength\b|etch(ing)? is (finished|done|over|complete)|finished etching|when to stop|light\b.*\b(chamber|plasma)\b/i,
      /\b(punto final|emisi[oó]n|emisiones|emiten?|espectr[oó]metr\w*|espectroscop[ií]a|espectro|brilla|resplandor|excitaci[oó]n|temperatura electr[oó]nica|l[ií]neas? espectral(es)?|longitud de onda|violeta|morado|rosa)\b|\bcolor(es)?\b|termina el grabado|cu[aá]ndo (termina|parar|detener)|luz del plasma/i,
      /终点|終點|发射光谱|發射光譜|光谱|光譜|辉光|輝光|发光|發光|激发|激發|电子温度|電子溫度|谱线|譜線|波长|波長|紫色|粉色|什么颜色|什麼顏色|刻蚀完|蝕刻完/,
    ],
  },
  {
    id: 'sheath',
    who: ['sarah'],
    match: [
      /\b(sheath|ion energy|vertical)\b|why are the ions|\bions?\b.*\benergy\b|\benergy\b.*\bions?\b/i,
      /\b(vaina|energ[ií]a de los iones|vertical(es)?)\b|\biones\b(?!.*\b(emit|luz|brill))/i,
      /鞘层|鞘層|离子能量|離子能量|垂直/,
    ],
  },
  {
    id: 'trench-arde',
    who: ['sarah'],
    match: [
      /\b(ARDE|aspect ratio|narrow\w*|lag|trench\w*)\b/i,
      /\b(ARDE|relaci[oó]n de aspecto|zanjas?|estrech\w*)\b/i,
      /深宽比|深寬比|变窄|變窄|窄沟|窄溝|沟槽|溝槽|滞后|滯後/,
    ],
  },
  {
    id: 'thermal-budget',
    who: ['tom'],
    match: [
      /\b(thermal budget|anneal\w*|spread(s|ing)? out)\b/i,
      /\b(presupuesto t[eé]rmico|recocidos?)\b/i,
      /热预算|熱預算|退火|扩散开|擴散開/,
    ],
  },
  {
    id: 'drive-in',
    who: ['tom'],
    match: [
      /^(?!.*\bdiffus).*\b(drive-?in|junction depth|junction|furnace|profile|boron)\b/i,
      /^(?!.*(difund|difusi)).*\b(uni[oó]n|horno|perfil|boro|profundidad de uni[oó]n)\b/i,
      /推进|推進|结深|結深|扩散炉|擴散爐|炉管|爐管/,
    ],
  },
  // PRB-14's own: the channel under every gate on the wafer she sorts.
  {
    id: 'channel-quantisation',
    who: ['grace'],
    match: [
      /\b(inversion layers?|subbands?|channel quanti[sz]\w*|schr(ö|o)dinger.?poisson|effective oxide|dark space|gate capacitance|electron centroid|centroid)\b|quantum\b.*\b(channel|gate|inversion)\b|\b(channel|gate|inversion)\b.*\bquantum\b|threshold (shift|voltage)|why .*\bthreshold\b|\bc.?v curves?\b|(electrons?|charge)\b.*\b(away from|stand\w* off|set back from)\b.*\b(interface|oxide)\b|capacitance\b.*\b(lower|less|smaller)\b/i,
      /capa de inversi[oó]n|subbandas?|cuantizaci[oó]n del canal|schr(ö|o)dinger.?poisson|umbral|[oó]xido efectivo|capacitancia de (la )?compuerta|curva c.?v|centroide|cu[aá]ntic\w*\b.*\bcanal|canal\b.*\bcu[aá]ntic|se alejan de la (interfaz|interfase)|lejos de la (interfaz|interfase)/i,
      /反型层|反轉層|子能带|子能帶|沟道量子化|通道量子化|薛定谔.?泊松|薛丁格.?帕松|阈值|臨界電壓|等效氧化层|等效氧化層|栅电容|閘極電容|电容.?电压曲线|電容.?電壓曲線|形心|量子.*(沟道|通道|溝道)|(沟道|通道|溝道).*量子|离界面|離界面|离开界面|離開界面/,
    ],
  },
  // The line: the building as a queue. Kevin's, because it is the one picture
  // that belongs to nobody's tool - WIP, cycle time, and what the chiller trip
  // did to both. See sim/line.
  {
    id: 'line-chiller',
    who: ['kevin'],
    match: [
      /\b(line|queues?|backlog|WIP|cycle time|throughput|chiller|lots? (stack|pil)\w*|little'?s law)\b/i,
      /\b(cola|l[ií]nea|tiempo de ciclo|enfriador|lotes acumulados)\b/i,
      /队列|排队|產線|产线|周期时间|週期時間|冷水机|冰水機|佇列/,
    ],
  },
  {
    id: 'bay-airflow',
    who: ['kevin'],
    match: [
      /\b(airflow|downflow|laminar|particles?|clean ?room)\b/i,
      /\b(flujo de aire|aire|laminar|part[ií]culas?|sala limpia)\b/i,
      /气流|氣流|层流|層流|颗粒|顆粒|洁净|潔淨/,
    ],
  },
]

const byId = new Map(DEMOS.map((d) => [d.id, d]))

/** What this person is allowed to put on the screen. */
export function demosFor(castId: string): DemoDef[] {
  return DEMOS.filter((d) => d.who === 'all' || d.who.includes(castId))
}

export const demoById = (id: string): DemoDef | undefined => byId.get(id)

/**
 * The marker a technician ends a reply with when they are reaching for a
 * scene: `[[show:sheath]]`.
 *
 * Lenient about spacing and case because a model writing prose will not be
 * careful about either, and a marker that fails to parse leaves its brackets
 * in the middle of a spoken sentence.
 */
const SHOW = /\[\[\s*show\s*:\s*([a-z0-9-]+)\s*\]\]/gi

/**
 * Take the scene out of a reply.
 *
 * EVERY marker comes out of the text, including one naming a scene this
 * person was not offered: the sentence is about to be read aloud, and
 * brackets are not speech. At most one marker survives as a scene, and only
 * if `allowed` - the very list that went out in the brief - contains it.
 * Nothing else in the app decides what may mount.
 */
export function takeDemo(text: string, allowed: readonly string[]): { text: string; demo?: string } {
  let demo: string | undefined
  const stripped = text
    .replace(SHOW, (_match, id: string) => {
      const wanted = String(id).toLowerCase()
      if (!demo && allowed.includes(wanted)) demo = wanted
      return ''
    })
    // A marker on its own line leaves a hole where it stood.
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
  return demo ? { text: stripped, demo } : { text: stripped }
}

/**
 * Which scene an offline technician reaches for, or none.
 *
 * Takes the same `allowed` list `takeDemo` does - the brief's own - rather
 * than a cast id, so both paths to a scene are gated by one thing and the
 * brief stays free of internal handles.
 *
 * THE PERSON'S OWN SCENES ARE TRIED FIRST (2026-09-18), then the principles
 * everyone can show, each group in registry order. It used to be registry
 * order alone, principles first, on the theory that "a diffusion question at
 * the furnace is a diffusion question" - and it is, which is why a tool's
 * patterns must not claim the principle's bare words. But the other way
 * round was worse: "quantum", "lattice", "absorb" and "barrier" are
 * principle words that a tool's own scene ALSO uses, so Grace's channel,
 * Laura's phonons and Jake's resist kept losing their own questions to a
 * generic scene tried ahead of them. `allowed` is one person's list, so
 * every scene in it that is not `'all'` is theirs.
 */
export function matchDemo(allowed: readonly string[], question: string): string | undefined {
  const mine = DEMOS.filter((d) => d.who !== 'all' && allowed.includes(d.id))
  const shared = DEMOS.filter((d) => d.who === 'all' && allowed.includes(d.id))
  for (const demo of [...mine, ...shared]) {
    if (demo.match.some((re) => re.test(question))) return demo.id
  }
  return undefined
}
